import {
	useGetExternalIntegrationUserMapsQuery,
	useRegisterUserMutation,
	useUserOperationsQuery,
} from '@Queries/index';
import { warningDark } from '@Styles/colors';
import { baseStyleSheet } from '@Styles/styles';
import * as Api from '@ViewModels';
import { AutoCompleteSearchFieldAccessoryViewMode } from '@WebComponents/autocomplete/AutoCompleteSearchField';
import { ISimpleAutoCompleteSearchFieldItemSelectionEvent } from '@WebComponents/autocomplete/SimpleAutoCompleteSearchField';
import { Badge } from '@WebComponents/dataBoards/Badge';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '@WebComponents/DataGrid';
import { LoadingSpinner } from '@WebComponents/LoadingSpinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@WebComponents/Select';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { IAddEmployeeResult } from '../../../../../../../models/AdminModels';
import { getDisplayName } from '../../../../../../../models/NameUtils';
import { AddEmployeeModal } from '../../../../../../components/user/AddEmployee';
import { SettingGroup } from '../SettingsView/components/SettingGroup';
import { EditModeButton, EditModeSimpleAutoCompleteSearchField } from '../SettingsView/context';
import { styleSheet } from './styles';

interface IUserMappingsViewProps {
	impersonationContext: Api.IImpersonationContext;
	userMapsQuery: ReturnType<typeof useGetExternalIntegrationUserMapsQuery>;
	userMaps: Api.IIntegrationUser[];
	setUserMaps: (userMaps: Api.IIntegrationUser[]) => void;
}

export const UserMappingsView: React.FC<IUserMappingsViewProps> = ({
	impersonationContext,
	userMapsQuery,
	userMaps,
	setUserMaps,
}) => {
	const userOperationsQuery = useUserOperationsQuery({
		accountId: impersonationContext.account.id,
	});
	const registerUserMutation = useRegisterUserMutation({
		onSuccess: () => {
			toast.success('User added successfully');
		},
	});
	const accountUsers = (userOperationsQuery.data?.values ?? []) as Api.IUserWithStats[];

	React.useEffect(() => {
		setUserMaps(userMapsQuery.data ?? []);
	}, [setUserMaps, userMapsQuery.data]);

	const isLoading = userMapsQuery.isPending || userOperationsQuery.isPending;

	const [showAddEmployee, setShowAddEmployee] = React.useState(false);
	const [filteredConnectionType, setFilteredConnectionType] = React.useState<string | null>(null);

	// Get unique connection types from user maps
	const uniqueConnectionTypes = React.useMemo(() => {
		const types = new Set<string>();
		userMapsQuery.data?.forEach(item => {
			item.connectionTypes?.forEach(type => types.add(type));
		});
		return Array.from(types).sort();
	}, [userMapsQuery.data]);

	const connectionTypeOptions = React.useMemo(
		() => [
			{ value: 'all', label: 'All Types' },
			...uniqueConnectionTypes.map(type => ({
				label: type,
				value: type,
			})),
		],
		[uniqueConnectionTypes]
	);

	const onConnectionTypeFilterChanged = (value: string) => {
		if (value === 'all') {
			setFilteredConnectionType(null);
			return;
		}
		setFilteredConnectionType(value);
	};

	const onAddEmployeeModalRequestClose = (result?: IAddEmployeeResult, cancel?: boolean) => {
		setShowAddEmployee(false);
		if (!cancel && !!result && !!result.user) {
			registerUserMutation.mutate({
				accountId: impersonationContext.account.id,
				sendWelcomeEmail: result.sendWelcomeEmail,
				user: result.user,
			});
		}
	};

	const onAddEmployee = () => setShowAddEmployee(true);

	const clearLevitateUser = (index: number) => {
		const updatedUserMaps = userMaps.map((user, i) => {
			return i !== index
				? user
				: {
						...user,
						userId: null,
					};
		});
		setUserMaps(updatedUserMaps);
	};

	const onUserSelected = (e: ISimpleAutoCompleteSearchFieldItemSelectionEvent<Api.IUser>, index: number) => {
		const updatedUserMaps = userMaps.map((user, i) => {
			return i !== index
				? user
				: {
						...user,
						userId: e.selection.id,
					};
		});
		setUserMaps(updatedUserMaps);
	};

	const onCreateAutoCompleteViewModel = (
		_: Api.ResourceAutoCompleteViewModelType,
		suggestedViewModel: Api.ResourceAutoCompleteViewModel
	) => {
		suggestedViewModel.addParam({ includeDeactivated: false });
		return suggestedViewModel.impersonate({
			account: { id: impersonationContext.account.id },
		});
	};

	const filteredUserMaps = React.useMemo(() => {
		return userMaps.filter(item => {
			if (!filteredConnectionType) return true;
			return item.connectionTypes.includes(filteredConnectionType);
		});
	}, [userMaps, filteredConnectionType]);

	return (
		<SettingGroup title='User Mappings'>
			<div className={css(styleSheet.subtitle)}>
				Manage user mappings for this integration to control which Levitate user is associated with each user in the
				external system. These mappings determine ownership of contacts, writebacks (if the integration supports it),
				and other data. Unmapped users may result in archived contacts or missing data. Changes to mappings will trigger
				a new data extract from the source system.
			</div>
			<div className={css(styleSheet.actionBar)}>
				<div className={css(styleSheet.filterSection)}>
					<span className={css(styleSheet.filterLabel)}>Connection Type:</span>
					<Select
						items={connectionTypeOptions}
						value={filteredConnectionType || 'all'}
						onValueChange={onConnectionTypeFilterChanged}
					>
						<SelectTrigger>
							<SelectValue placeholder={filteredConnectionType ? filteredConnectionType : 'All Types'} />
						</SelectTrigger>
						<SelectContent>
							{connectionTypeOptions.map(option => (
								<SelectItem key={option.value} value={option.value}>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				<div className={css(styleSheet.buttonGroup)}>
					<EditModeButton displayValue='Add Employee' onClick={onAddEmployee} />
				</div>
			</div>

			<div className={css(styleSheet.tableContainer)}>
				<DataGrid styles={[styleSheet.tableOverrides]}>
					<DataGridColumns styles={[styleSheet.columnOverrides]}>
						<DataGridColumn sticky>Name</DataGridColumn>
						<DataGridColumn sticky>Connection Types</DataGridColumn>
						<DataGridColumn sticky>Levitate User</DataGridColumn>
					</DataGridColumns>

					<DataGridRows>
						{!isLoading &&
							filteredUserMaps.map((item, index) => {
								const levitateUser = accountUsers.find(user => {
									return user.id === item.userId;
								});

								return (
									<DataGridRow key={`row-${item.displayName}-${item.providerId}`}>
										<DataGridRowItem>
											<div className={css(styleSheet.userNameCell)}>
												<h6 className={css(baseStyleSheet.truncateText, baseStyleSheet.m0)}>{item.displayName}</h6>
												{item.isActive === false && <Badge text='Deactivated' foregroundColor={warningDark} />}
											</div>
										</DataGridRowItem>
										<DataGridRowItem>
											{item.connectionTypes.map(type => (
												<Badge key={`badge-${type}`} text={type} foregroundColor='#9E9E9E' />
											))}
										</DataGridRowItem>
										<DataGridRowItem>
											<EditModeSimpleAutoCompleteSearchField
												displayValue={levitateUser ? getDisplayName(levitateUser) : item.userId}
												type={Api.ResourceAutoCompleteViewModelType.User}
												// @ts-ignore
												onItemSelected={(e: ISimpleAutoCompleteSearchFieldItemSelectionEvent<Api.IUser>) =>
													onUserSelected(e, index)
												}
												dropdownContentItemsStyles={[styleSheet.autoCompleteDropdownItems]}
												dropdownContentStyle={styleSheet.autoCompleteDropdownContent}
												initialSearchQuery={levitateUser?.id ? getDisplayName(levitateUser) : ''}
												onClear={() => clearLevitateUser(index)}
												rightAccessoryViewMode={AutoCompleteSearchFieldAccessoryViewMode.Always}
												style={styleSheet.userInput}
												textAlign='left'
												resultsLimit={5}
												pageSize={5}
												placeholder='Search name'
												onCreateAutoCompleteViewModel={onCreateAutoCompleteViewModel}
												customStyles={[styleSheet.readOnlyText]}
											/>
										</DataGridRowItem>
									</DataGridRow>
								);
							})}

						{isLoading ? (
							<DataGridRow>
								<DataGridRowItem fullColSpan>
									<LoadingSpinner type='large' />
								</DataGridRowItem>
							</DataGridRow>
						) : null}
					</DataGridRows>
				</DataGrid>
			</div>

			<AddEmployeeModal
				modalProps={{
					isOpen: showAddEmployee,
					onRequestClose: onAddEmployeeModalRequestClose,
				}}
			/>
		</SettingGroup>
	);
};
